import type {
    CloudAuditContentResponse,
    CloudAuditEvent,
    CloudAuditListResponse,
    CloudAuditRun,
    CloudAuditRunListResponse,
    CloudAuditRunConversationMessage,
    CloudAuditRunConversationResponse,
    CloudAuditTimelineEvent,
} from '@common/api/agentRunLedgerService';

const KNOWN_PLATFORM_LABELS: Record<string, string> = {
    shopify: 'Shopify',
    prestashop: 'PrestaShop',
    bigcommerce: 'BigCommerce',
    woocommerce: 'WooCommerce',
    magento: 'Magento',
    wix: 'Wix',
    google_ads: 'Google Ads',
    'google-ads': 'Google Ads',
    meta_ads: 'Meta Ads',
    'meta-ads': 'Meta Ads',
    tiktok_ads: 'TikTok Ads',
    'tiktok-ads': 'TikTok Ads',
    google_analytics: 'Google Analytics',
    'google-analytics': 'Google Analytics',
    google_search_console: 'Google Search Console',
    'google-search-console': 'Google Search Console',
};

const KNOWN_STATUS_LABELS: Record<string, string> = {
    success: 'Completed',
    succeeded: 'Completed',
    complete: 'Completed',
    completed: 'Completed',
    failed: 'Failed',
    error: 'Failed',
    running: 'In progress',
    in_progress: 'In progress',
    pending: 'Pending',
    skipped: 'Skipped',
};

const KNOWN_OPERATION_LABELS: Record<string, string> = {
    read: '读取',
    create: '创建',
    update: '更新',
    delete: '删除',
    check: '检查',
};

const INTERNAL_FALLBACK_PLATFORMS = new Set(['bustly']);
const INTERNAL_FALLBACK_PATTERNS = [
    /(^|[^a-z0-9])local_command([^a-z0-9]|$)/i,
    /(^|[^a-z0-9])file([^a-z0-9]|$)/i,
    /(^|[^a-z0-9])terminal([^a-z0-9]|$)/i,
    /(^|[^a-z0-9])shell([^a-z0-9]|$)/i,
    /(^|[^a-z0-9])command([^a-z0-9]|$)/i,
    /(^|[^a-z0-9])runtime([^a-z0-9]|$)/i,
    /(^|[^a-z0-9])trace([^a-z0-9]|$)/i,
];

const textOrNull = (value?: string | null): string | null => {
    const text = String(value || '').trim();
    return text || null;
};

const stringList = (value?: string[] | null): string[] => (
    (value || []).map((item) => String(item || '').trim()).filter(Boolean)
);

const numberOrZero = (value: unknown): number => (
    typeof value === 'number' && Number.isFinite(value) ? value : 0
);

export type CloudAuditContentAvailability = 'available' | 'not-captured' | 'redacted' | 'deleted' | 'unknown';

export interface CloudAgentRunAuditTimelineEventView {
    id: string;
    eventAt: string;
    actionLabel: string;
    description: string | null;
    platformLabel: string | null;
    operationLabel: string | null;
    resourceLabel: string | null;
    applicationAccess: 'read' | 'write' | null;
    connectionDisplayName: string | null;
    statusLabel: string;
    changedFields: string[];
    contentAvailability: CloudAuditContentAvailability;
}

export interface CloudAgentRunAuditApplicationView {
    platformLabel: string;
    readCount: number;
    writeCount: number;
}

export interface CloudAgentRunAuditRunView {
    id: string;
    title: string;
    sourceTask: string | null;
    statusLabel: string;
    actorDisplayName: string | null;
    agentLabel: string | null;
    startedAt: string | null;
    endedAt: string | null;
    lastEventAt: string | null;
    eventCount: number;
    businessEventCount: number;
    readCount: number;
    writeCount: number;
    conversationAvailability: CloudAuditContentAvailability;
    applications: CloudAgentRunAuditApplicationView[];
    timeline: CloudAgentRunAuditTimelineEventView[];
}

export interface CloudAgentRunAuditListView {
    workspaceId: string;
    limit: number;
    runs: CloudAgentRunAuditRunView[];
}

export interface CloudAgentRunAuditContentView {
    contentKind: CloudAuditContentResponse['contentKind'];
    contentSchemaVersion: number;
    payload: unknown;
    capturedAt: string;
}

export interface CloudAgentRunAuditConversationMessageView {
    id: string;
    role: string;
    source: string | null;
    messageAt: string | null;
    contentText: string;
}

export type CloudAgentRunAuditConversationItemView =
    | {
        kind: 'message';
        id: string;
        message: CloudAgentRunAuditConversationMessageView;
    }
    | {
        kind: 'agent-execution';
        id: string;
        messages: CloudAgentRunAuditConversationMessageView[];
    };

export interface CloudAgentRunAuditConversationView {
    sourceTask: string | null;
    actorDisplayName: string | null;
    firstMessageAt: string | null;
    lastMessageAt: string | null;
    messageCount: number;
    messages: CloudAgentRunAuditConversationMessageView[];
}

export interface CloudAgentRunAuditFilters {
    query: string;
    platform: string | null;
    status: string | null;
    operation: string | null;
    member: string | null;
}

export interface CloudAgentRunAuditEventView {
    id: string;
    eventAt: string;
    summaryText: string;
    platformLabel: string | null;
    connectionDisplayName: string | null;
    resourceLabel: string | null;
    operationLabel: string | null;
    statusLabel: string;
    changedFields: string[];
    contentAvailability: CloudAuditContentAvailability;
    isSystemEvent: boolean;
}

const resolveContentAvailability = (state?: string | null): CloudAuditContentAvailability => {
    switch (textOrNull(state)?.toLowerCase()) {
        case 'available': return 'available';
        case 'none': return 'not-captured';
        case 'redacted': return 'redacted';
        case 'deleted': return 'deleted';
        default: return 'unknown';
    }
};

export const resolveCloudAuditPlatformLabel = (event: Pick<
    CloudAuditEvent,
    'platformLabel' | 'integration' | 'connection'
>): string | null => {
    const serverLabel = textOrNull(event.platformLabel) || textOrNull(event.connection?.platformLabel);
    if (serverLabel) return serverLabel;

    const fallbackKey = textOrNull(event.integration) || textOrNull(event.connection?.platform);
    return fallbackKey ? KNOWN_PLATFORM_LABELS[fallbackKey.toLowerCase()] || null : null;
};

const resolveTimelinePlatformLabel = (event: Pick<CloudAuditTimelineEvent, 'platformLabel'>): string | null => {
    const serverLabel = textOrNull(event.platformLabel);
    if (serverLabel) return serverLabel;
    return null;
};

const resolveStatusLabel = (event: Pick<CloudAuditEvent | CloudAuditRun | CloudAuditTimelineEvent, 'statusLabel' | 'status'>): string => {
    const serverLabel = textOrNull(event.statusLabel);
    if (serverLabel) return serverLabel;
    const fallback = textOrNull(event.status);
    return fallback ? KNOWN_STATUS_LABELS[fallback.toLowerCase()] || 'Status unavailable' : 'Status unavailable';
};

const resolveOperationLabel = (event: Pick<CloudAuditEvent | CloudAuditTimelineEvent, 'operationLabel' | 'operationKind'>): string | null => {
    const serverLabel = textOrNull(event.operationLabel);
    if (serverLabel) return serverLabel;
    const fallback = textOrNull(event.operationKind);
    return fallback ? KNOWN_OPERATION_LABELS[fallback.toLowerCase()] || null : null;
};

export const adaptCloudAgentRunAuditEvent = (event: CloudAuditEvent): CloudAgentRunAuditEventView => {
    const platformLabel = resolveCloudAuditPlatformLabel(event);

    return {
        id: event.id,
        eventAt: event.eventAt,
        summaryText: textOrNull(event.summaryText) || 'Audit event',
        platformLabel,
        connectionDisplayName: event.connection?.resolved ? textOrNull(event.connection.displayName) : null,
        resourceLabel: textOrNull(event.resourceLabel),
        operationLabel: resolveOperationLabel(event),
        statusLabel: resolveStatusLabel(event),
        changedFields: stringList(event.changedFields),
        contentAvailability: resolveContentAvailability(event.contentPayloadState),
        isSystemEvent: platformLabel?.toLowerCase() === 'bustly',
    };
};

export const adaptCloudAgentRunAuditList = (
    response: CloudAuditListResponse,
): { workspaceId: string; limit: number; events: CloudAgentRunAuditEventView[]; records: never[] } => ({
    workspaceId: response.workspaceId,
    limit: response.limit,
    events: (response.events || []).map(adaptCloudAgentRunAuditEvent),
    records: [],
});

const eventApplicationAccess = (event: CloudAuditEvent): 'read' | 'write' | null => {
    const operation = textOrNull(event.operationKind)?.toLowerCase();
    if (operation === 'read' || operation === 'check') return 'read';
    if (operation === 'create' || operation === 'update' || operation === 'delete') return 'write';
    if (event.changedFields?.length) return 'write';
    return null;
};

const includesInternalFallbackToken = (event: CloudAuditEvent): boolean => {
    const searchText = [
        event.summaryText,
        event.resourceKind,
        event.resourceLabel,
        event.operationKind,
        event.operationLabel,
        event.toolName,
        event.targetKind,
        event.auditSurface,
        event.auditCoverage,
        ...(event.sourceKinds || []),
    ].map((value) => String(value || '').toLowerCase()).join(' ');

    return INTERNAL_FALLBACK_PATTERNS.some((pattern) => pattern.test(searchText));
};

const isReadableFallbackBusinessEvent = (event: CloudAuditEvent): boolean => {
    const platformLabel = resolveCloudAuditPlatformLabel(event);
    if (!platformLabel) return false;
    if (INTERNAL_FALLBACK_PLATFORMS.has(platformLabel.toLowerCase())) return false;
    if (includesInternalFallbackToken(event)) return false;

    const status = textOrNull(event.status)?.toLowerCase();
    const statusLabel = textOrNull(event.statusLabel)?.toLowerCase();
    if (status === 'started' || statusLabel === 'started') return false;

    return true;
};

const adaptListEventToTimelineEvent = (event: CloudAuditEvent): CloudAgentRunAuditTimelineEventView => ({
    id: event.id,
    eventAt: event.eventAt,
    actionLabel: textOrNull(event.summaryText) || 'Audit event',
    description: textOrNull(event.summaryText),
    platformLabel: resolveCloudAuditPlatformLabel(event),
    operationLabel: resolveOperationLabel(event),
    resourceLabel: textOrNull(event.resourceLabel),
    applicationAccess: eventApplicationAccess(event),
    connectionDisplayName: event.connection?.resolved ? textOrNull(event.connection.displayName) : null,
    statusLabel: resolveStatusLabel(event),
    changedFields: stringList(event.changedFields),
    contentAvailability: resolveContentAvailability(event.contentPayloadState),
});

export const adaptCloudAgentRunAuditEventListToRunList = (
    response: CloudAuditListResponse,
): CloudAgentRunAuditListView => {
    const grouped = new Map<string, CloudAuditEvent[]>();
    (response.events || []).filter(isReadableFallbackBusinessEvent).forEach((event, index) => {
        const key = textOrNull(event.runId) || `run:${index}`;
        grouped.set(key, [...(grouped.get(key) || []), event]);
    });

    const runs: CloudAgentRunAuditRunView[] = Array.from(grouped.entries()).map(([runId, events], index) => {
        const sortedEvents = [...events].sort((first, second) => first.eventAt.localeCompare(second.eventAt));
        const lastEvent = sortedEvents[sortedEvents.length - 1];
        const firstEvent = sortedEvents[0];
        const timeline = sortedEvents.map(adaptListEventToTimelineEvent);
        const platformCounts = new Map<string, { readCount: number; writeCount: number }>();
        timeline.forEach((event) => {
            if (!event.platformLabel) return;
            const current = platformCounts.get(event.platformLabel) || { readCount: 0, writeCount: 0 };
            if (event.applicationAccess === 'write') current.writeCount += 1;
            else if (event.applicationAccess === 'read') current.readCount += 1;
            platformCounts.set(event.platformLabel, current);
        });
        const applications = Array.from(platformCounts.entries()).map(([platformLabel, counts]) => ({
            platformLabel,
            readCount: counts.readCount,
            writeCount: counts.writeCount,
        }));
        const readCount = timeline.filter((event) => event.applicationAccess === 'read').length;
        const writeCount = timeline.filter((event) => event.applicationAccess === 'write').length;

        return {
            id: runId || `run:${index}`,
            title: textOrNull(lastEvent?.summaryText) || textOrNull(firstEvent?.summaryText) || 'Agent 执行',
            sourceTask: null,
            statusLabel: lastEvent ? resolveStatusLabel(lastEvent) : 'Status unavailable',
            actorDisplayName: null,
            agentLabel: null,
            startedAt: textOrNull(firstEvent?.eventAt),
            endedAt: textOrNull(lastEvent?.eventAt),
            lastEventAt: textOrNull(lastEvent?.eventAt),
            eventCount: sortedEvents.length,
            businessEventCount: timeline.length,
            readCount,
            writeCount,
            conversationAvailability: 'unknown',
            applications,
            timeline,
        };
    });

    return {
        workspaceId: response.workspaceId,
        limit: response.limit,
        runs,
    };
};

const adaptTimelineEvent = (event: CloudAuditTimelineEvent): CloudAgentRunAuditTimelineEventView => ({
    id: event.id,
    eventAt: event.eventAt,
    actionLabel: textOrNull(event.actionLabel) || 'Audit event',
    description: textOrNull(event.description),
    platformLabel: resolveTimelinePlatformLabel(event),
    operationLabel: resolveOperationLabel(event),
    resourceLabel: textOrNull(event.resourceLabel),
    applicationAccess: event.applicationAccess === 'read' || event.applicationAccess === 'write'
        ? event.applicationAccess
        : null,
    connectionDisplayName: event.connection?.resolved ? textOrNull(event.connection.displayName) : null,
    statusLabel: resolveStatusLabel(event),
    changedFields: stringList(event.changedFields),
    contentAvailability: resolveContentAvailability(event.contentPayloadState),
});

const adaptApplication = (application: CloudAuditRun['applications'][number]): CloudAgentRunAuditApplicationView | null => {
    const platformLabel = textOrNull(application.platformLabel)
        || (textOrNull(application.platform) ? KNOWN_PLATFORM_LABELS[String(application.platform).toLowerCase()] : null);
    if (!platformLabel) return null;
    return {
        platformLabel,
        readCount: numberOrZero(application.readCount),
        writeCount: numberOrZero(application.writeCount),
    };
};

const uniqueRunId = (run: CloudAuditRun, index: number): string => (
    textOrNull(run.runId) || `run:${index}`
);

const adaptRun = (run: CloudAuditRun, index: number): CloudAgentRunAuditRunView => ({
    id: uniqueRunId(run, index),
    title: textOrNull(run.title) || 'Agent 执行',
    sourceTask: textOrNull(run.sourceTask),
    statusLabel: resolveStatusLabel(run),
    actorDisplayName: textOrNull(run.actor?.displayName),
    agentLabel: textOrNull(run.agent?.label),
    startedAt: textOrNull(run.startedAt),
    endedAt: textOrNull(run.endedAt),
    lastEventAt: textOrNull(run.lastEventAt),
    eventCount: numberOrZero(run.eventCount),
    businessEventCount: numberOrZero(run.businessEventCount),
    readCount: numberOrZero(run.readCount),
    writeCount: numberOrZero(run.writeCount),
    conversationAvailability: resolveContentAvailability(run.conversationContentState),
    applications: (run.applications || []).map(adaptApplication).filter((item): item is CloudAgentRunAuditApplicationView => Boolean(item)),
    timeline: (run.timeline || []).map(adaptTimelineEvent),
});

export const adaptCloudAgentRunAuditRunList = (
    response: CloudAuditRunListResponse,
): CloudAgentRunAuditListView => ({
    workspaceId: response.workspaceId,
    limit: response.limit,
    runs: (response.runs || []).map(adaptRun),
});

export const adaptCloudAgentRunAuditContent = (
    response: CloudAuditContentResponse,
): CloudAgentRunAuditContentView => ({
    contentKind: response.contentKind,
    contentSchemaVersion: response.contentSchemaVersion,
    payload: response.payload,
    capturedAt: response.capturedAt,
});

const adaptConversationMessage = (
    message: CloudAuditRunConversationMessage,
    index: number,
): CloudAgentRunAuditConversationMessageView | null => {
    const contentText = textOrNull(message.contentText);
    if (!contentText) return null;
    return {
        id: textOrNull(message.messageId) || `message:${index}`,
        role: textOrNull(message.role) || 'message',
        source: textOrNull(message.source),
        messageAt: textOrNull(message.messageAt),
        contentText,
    };
};

export const adaptCloudAgentRunAuditConversation = (
    response: CloudAuditRunConversationResponse,
): CloudAgentRunAuditConversationView => ({
    sourceTask: textOrNull(response.sourceTask),
    actorDisplayName: textOrNull(response.actor?.displayName),
    firstMessageAt: textOrNull(response.firstMessageAt),
    lastMessageAt: textOrNull(response.lastMessageAt),
    messageCount: numberOrZero(response.messageCount),
    messages: (response.messages || [])
        .map(adaptConversationMessage)
        .filter((message): message is CloudAgentRunAuditConversationMessageView => Boolean(message)),
});

const isMemberConversationMessage = (message: CloudAgentRunAuditConversationMessageView): boolean => {
    const role = message.role.trim().toLowerCase();
    return role === 'user' || role === 'member';
};

export const buildCloudAgentRunAuditConversationItems = (
    messages: CloudAgentRunAuditConversationMessageView[],
): CloudAgentRunAuditConversationItemView[] => {
    const items: CloudAgentRunAuditConversationItemView[] = [];
    let pendingAgentMessages: CloudAgentRunAuditConversationMessageView[] = [];

    const flushAgentMessages = () => {
        if (pendingAgentMessages.length === 0) return;

        const finalMessage = pendingAgentMessages[pendingAgentMessages.length - 1];
        if (!finalMessage) return;
        const executionMessages = pendingAgentMessages.slice(0, -1);
        const firstExecutionMessage = executionMessages[0];
        const lastExecutionMessage = executionMessages[executionMessages.length - 1];
        if (firstExecutionMessage && lastExecutionMessage) {
            items.push({
                kind: 'agent-execution',
                id: `agent-execution:${firstExecutionMessage.id}:${lastExecutionMessage.id}`,
                messages: executionMessages,
            });
        }
        items.push({ kind: 'message', id: finalMessage.id, message: finalMessage });
        pendingAgentMessages = [];
    };

    messages.forEach((message) => {
        if (isMemberConversationMessage(message)) {
            flushAgentMessages();
            items.push({ kind: 'message', id: message.id, message });
            return;
        }
        pendingAgentMessages.push(message);
    });
    flushAgentMessages();

    return items;
};

export const getVisibleCloudAgentRunAuditConversationMessages = (
    messages: CloudAgentRunAuditConversationMessageView[],
): CloudAgentRunAuditConversationMessageView[] => (
    buildCloudAgentRunAuditConversationItems(messages).flatMap((item) => (
        item.kind === 'message' ? [item.message] : []
    ))
);

const includesQuery = (value: string | null, query: string): boolean => (
    !query || Boolean(value?.toLocaleLowerCase().includes(query))
);

export const filterCloudAgentRunAuditRuns = (
    runs: CloudAgentRunAuditRunView[],
    filters: CloudAgentRunAuditFilters,
): CloudAgentRunAuditRunView[] => {
    const query = filters.query.trim().toLocaleLowerCase();

    return runs.filter((run) => (
        (!filters.platform || run.applications.some((application) => application.platformLabel === filters.platform)
            || run.timeline.some((event) => event.platformLabel === filters.platform))
        && (!filters.status || run.statusLabel === filters.status)
        && (!filters.operation || run.timeline.some((event) => event.operationLabel === filters.operation))
        && (!filters.member || run.actorDisplayName === filters.member)
        && [
            run.title,
            run.sourceTask,
            run.actorDisplayName,
            run.agentLabel,
            ...run.applications.map((application) => application.platformLabel),
            ...run.timeline.flatMap((event) => [
                event.actionLabel,
                event.description,
                event.platformLabel,
                event.connectionDisplayName,
                event.resourceLabel,
            ]),
        ].some((value) => includesQuery(value, query))
    ));
};

export const cloudAuditRunFilterOptions = (
    runs: CloudAgentRunAuditRunView[],
    key: 'platform' | 'status' | 'operation' | 'member',
): string[] => {
    const values = runs.flatMap((run) => {
        if (key === 'platform') return run.applications.map((application) => application.platformLabel);
        if (key === 'status') return [run.statusLabel];
        if (key === 'operation') return run.timeline.map((event) => event.operationLabel).filter((value): value is string => Boolean(value));
        return run.actorDisplayName ? [run.actorDisplayName] : [];
    });
    return Array.from(new Set(values)).sort((first, second) => first.localeCompare(second));
};
